/**
 * Per-domain runtime config, resolved at the edge from the request's `Host`
 * header and returned as a synthetic response — `/config.json` never reaches
 * S3 and is never committed to `public/`.
 *
 * This is an envelope. Only `mapboxToken` ships today; `recaptchaSiteKey`,
 * `sentryDsn` and `stripePublishableKey` are expected to follow, so nothing
 * here should assume a single key.
 *
 * Failures (404, timeout, malformed body) resolve to `{}` rather than
 * rejecting. Consumers fall back to their build-time env var so the front end
 * and the edge function can deploy in either order.
 */
const CONFIG_URL = '/config.json';
const CONFIG_TIMEOUT_MS = 5000;

let configPromise;

const fetchConfig = async () => {
  let timeoutId;

  try {
    // AbortController rather than AbortSignal.timeout: the latter lands in
    // Chrome 103 / Safari 16, but our browserslist floor is chrome >= 67, so
    // roughly half the configured targets would throw here and fall through
    // to the empty config even when the request would have succeeded.
    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);

    const response = await fetch(CONFIG_URL, { signal: controller.signal });

    return response.ok ? await response.json() : {};
  } catch {
    // no-op — an unreachable or malformed config must not break the app
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Memoized so concurrent callers share a single request. Safe to call at app
 * entry to start the fetch early; the result must never be awaited above the
 * initial render or a stalled request becomes a blank screen.
 */
export const getConfig = () => {
  configPromise ??= fetchConfig();

  return configPromise;
};

export const resetConfig = () => {
  configPromise = undefined;
};
